// Internal-linking helpers — central place that decides which blog articles,
// cuisines and suburbs to surface from each page type.
import { BLOG_BY_SLUG, BLOG_CATEGORIES } from "./blog";
import { CUISINES, CUISINE_BY_SLUG } from "./cuisines";
import { SUBURBS, SUBURB_BY_SLUG } from "./suburbs";

const labelForCat = (id) =>
  BLOG_CATEGORIES.find((c) => c.id === id)?.label || "Article";

const blogItem = (slug) => {
  const p = BLOG_BY_SLUG[slug];
  if (!p) return null;
  return {
    to: `/blog/${p.slug}`,
    label: p.title,
    sublabel: `${labelForCat(p.category)} · ${p.readMin} min read`,
    blurb: p.dek,
    image: p.hero,
    cta: "Read article",
    testId: `rail-blog-${p.slug}`,
  };
};

const cuisineItem = (slug) => {
  const c = CUISINE_BY_SLUG[slug];
  if (!c) return null;
  return {
    to: `/cuisine/${c.slug}`,
    label: c.name,
    sublabel: "Cuisine specialist",
    image: c.hero,
    cta: `${c.short} specialist`,
    testId: `rail-cuisine-${c.slug}`,
  };
};

const suburbItem = (slug) => {
  const s = SUBURB_BY_SLUG[slug];
  if (!s) return null;
  return {
    to: `/restaurant-photographer/${s.slug}`,
    label: `Restaurant Photographer ${s.name}`,
    sublabel: "Melbourne coverage",
    image: s.hero,
    cta: `${s.name} coverage`,
    testId: `rail-suburb-${s.slug}`,
  };
};

// ─── SERVICE → BLOG MAPPING ────────────────────────────
// 3 hand-picked articles per service page.
const SERVICE_TO_BLOG = {
  "menu-photography": [
    "what-does-restaurant-photography-cost-melbourne",
    "how-to-prepare-restaurant-menu-shoot",
    "menu-photography-vs-food-photography-difference",
  ],
  "food-photography": [
    "menu-photography-vs-food-photography-difference",
    "melbourne-food-photography-styles-2026",
    "phone-vs-professional-restaurant-photography",
  ],
  "venue-photography": [
    "why-google-business-profile-photos-matter",
    "melbourne-restaurant-marketing-trends-2026",
    "melbourne-food-photography-styles-2026",
  ],
  "brand-photography": [
    "melbourne-restaurant-marketing-trends-2026",
    "melbourne-food-photography-styles-2026",
    "how-often-reshoot-restaurant-menu",
  ],
  "ubereats-photography": [
    "uber-eats-photo-guide-melbourne",
    "burger-photography-melbourne-uber-eats",
    "how-often-reshoot-restaurant-menu",
  ],
  "cafe-photography": [
    "cafe-photography-melbourne-guide",
    "melbourne-food-photography-styles-2026",
    "melbourne-restaurant-marketing-trends-2026",
  ],
  "google-business-profile-photos": [
    "why-google-business-profile-photos-matter",
    "melbourne-restaurant-marketing-trends-2026",
    "best-suburbs-restaurant-photography-melbourne",
  ],
  "restaurant-reels": [
    "melbourne-restaurant-marketing-trends-2026",
    "melbourne-food-photography-styles-2026",
    "phone-vs-professional-restaurant-photography",
  ],
  packages: [
    "what-does-restaurant-photography-cost-melbourne",
    "how-often-reshoot-restaurant-menu",
    "best-suburbs-restaurant-photography-melbourne",
  ],
};

export const blogsForService = (serviceKey) =>
  (SERVICE_TO_BLOG[serviceKey] || [
    "what-does-restaurant-photography-cost-melbourne",
    "melbourne-restaurant-marketing-trends-2026",
    "uber-eats-photo-guide-melbourne",
  ])
    .map(blogItem)
    .filter(Boolean);

// ─── SUBURB → CUISINE + BLOG ───────────────────────────
// Same 4 cuisines work for every suburb (most-shot cuisines in Melbourne).
const DEFAULT_SUBURB_CUISINES = ["indian-restaurants", "pizza-restaurants", "cafes", "burger-restaurants"];

export const cuisinesForSuburb = (_suburbSlug) =>
  DEFAULT_SUBURB_CUISINES.map(cuisineItem).filter(Boolean);

export const blogsForSuburb = (_suburbSlug) =>
  [
    "best-suburbs-restaurant-photography-melbourne",
    "what-does-restaurant-photography-cost-melbourne",
    "uber-eats-photo-guide-melbourne",
  ]
    .map(blogItem)
    .filter(Boolean);

// ─── CUISINE → SUBURB + BLOG ───────────────────────────
// Per-cuisine: 4 suburbs known for that cuisine + 3 hand-picked articles.
const CUISINE_TO_SUBURBS = {
  "indian-restaurants": ["footscray", "dandenong", "werribee", "point-cook"],
  "pizza-restaurants": ["carlton", "fitzroy", "brunswick", "richmond"],
  "burger-restaurants": ["fitzroy", "brunswick", "richmond", "northcote"],
  cafes: ["brunswick", "northcote", "fitzroy", "yarraville"],
  "dessert-shops": ["box-hill", "glen-waverley", "footscray", "dandenong"],
  catering: ["melbourne-cbd", "south-melbourne", "richmond", "brunswick"],
  "fine-dining": ["melbourne-cbd", "south-yarra", "richmond", "carlton"],
  bars: ["fitzroy", "melbourne-cbd", "richmond", "st-kilda"],
};

const CUISINE_TO_BLOG = {
  "indian-restaurants": [
    "indian-restaurant-photography-melbourne-guide",
    "uber-eats-photo-guide-melbourne",
    "what-does-restaurant-photography-cost-melbourne",
  ],
  "pizza-restaurants": [
    "pizza-photography-melbourne-cheese-pull",
    "uber-eats-photo-guide-melbourne",
    "how-to-prepare-restaurant-menu-shoot",
  ],
  "burger-restaurants": [
    "burger-photography-melbourne-uber-eats",
    "uber-eats-photo-guide-melbourne",
    "how-often-reshoot-restaurant-menu",
  ],
  cafes: [
    "cafe-photography-melbourne-guide",
    "melbourne-food-photography-styles-2026",
    "why-google-business-profile-photos-matter",
  ],
  "dessert-shops": [
    "melbourne-food-photography-styles-2026",
    "uber-eats-photo-guide-melbourne",
    "what-does-restaurant-photography-cost-melbourne",
  ],
  catering: [
    "what-does-restaurant-photography-cost-melbourne",
    "melbourne-restaurant-marketing-trends-2026",
    "best-suburbs-restaurant-photography-melbourne",
  ],
  "fine-dining": [
    "menu-photography-vs-food-photography-difference",
    "melbourne-food-photography-styles-2026",
    "how-often-reshoot-restaurant-menu",
  ],
  bars: [
    "melbourne-food-photography-styles-2026",
    "melbourne-restaurant-marketing-trends-2026",
    "why-google-business-profile-photos-matter",
  ],
};

export const suburbsForCuisine = (cuisineSlug) =>
  (CUISINE_TO_SUBURBS[cuisineSlug] || ["melbourne-cbd", "brunswick", "fitzroy", "footscray"])
    .map(suburbItem)
    .filter(Boolean);

export const blogsForCuisine = (cuisineSlug) =>
  (CUISINE_TO_BLOG[cuisineSlug] || [
    "what-does-restaurant-photography-cost-melbourne",
    "uber-eats-photo-guide-melbourne",
    "melbourne-restaurant-marketing-trends-2026",
  ])
    .map(blogItem)
    .filter(Boolean);

// Convenience: top 6 "all of Melbourne" suburb cards for hub-style rails.
export const topSuburbItems = () =>
  ["melbourne-cbd", "fitzroy", "brunswick", "footscray", "richmond", "dandenong"]
    .map(suburbItem)
    .filter(Boolean);

// Convenience: all 8 cuisines as items (e.g. for a "Cuisine specialist" rail).
export const allCuisineItems = () => CUISINES.map((c) => cuisineItem(c.slug)).filter(Boolean);

// Convenience: all 23 suburbs (rarely needed but handy).
export const allSuburbItems = () => SUBURBS.map((s) => suburbItem(s.slug)).filter(Boolean);
